import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import axios from 'axios';
import { GET_DIRECT_UPLOAD } from 'meeopp-graphql/modules/file-upload';
import { randomId } from 'utils/random-id';

const makeFileUploadId = (filename, fileExtension) => {
  if (!filename) {
    return randomId();
  }

  return `${filename}.${fileExtension}`;
};

const useDirectUpload = (action, options) => {
  const onUploadProgress = options?.onUploadProgress;
  const [fileUploadIdToBlob, setFileUploadIdToBlob] = useState(null);
  const [uploadPromiseControl, setUploadPromiseControl] = useState(null);

  const resetUpload = () => {
    setFileUploadIdToBlob(null);
    setUploadPromiseControl(null);
  };

  const onError = (error) => {
    uploadPromiseControl.reject(error);
    resetUpload();
  };

  const [getDirectUpload] = useLazyQuery(GET_DIRECT_UPLOAD, {
    fetchPolicy: 'no-cache',

    onCompleted: async (data) => {
      if (!data) {
        onError(new Error('Unknown error uploading file.'));
      }

      const directUploadResponses = data.getDirectUpload;
      try {
        const fileKeys = await Promise.all(
          directUploadResponses.map(async (response) => {
            const { fileKey, url, fields, fileUploadId } = response;
            const blob = fileUploadIdToBlob[fileUploadId];

            await performBlobUpload({
              url,
              fields,
              blob,
              onUploadProgress,
            });

            return fileKey;
          })
        );

        uploadPromiseControl.resolve(fileKeys);
      } catch (error) {
        onError(error);
      } finally {
        resetUpload();
      }
    },

    onError,
  });

  const initiateUpload = (blobRequests) => {
    if (uploadPromiseControl) {
      uploadPromiseControl.reject(new Error('Another upload is already in progress!'));
    }

    if (!action) throw new Error('Must provide a direct upload action name.');

    // Create the idToBlobMap and requests at the same time to ensure that the
    // fileUploadIds are the same (in case they are generated randomly).
    const [idToBlobMap, directUploadRequests] = blobRequests.reduce(
      (mapAndRequests, blobRequest) => {
        const [idToBlob, requests] = mapAndRequests;
        const { filename, fileExtension, blob } = blobRequest;

        const fileUploadId = makeFileUploadId(filename, fileExtension);
        idToBlob[fileUploadId] = blob;

        const newRequest = { fileUploadId, filename, fileExtension, inferredMimeType: blob.type };
        requests.push(newRequest);

        return [idToBlob, requests];
      },
      [{}, []]
    );

    setFileUploadIdToBlob(idToBlobMap);

    const uploadPromise = new Promise((resolve, reject) => {
      setUploadPromiseControl({ resolve, reject });
    });

    getDirectUpload({ variables: { action, directUploadRequests } });

    return uploadPromise;
  };

  return [initiateUpload];
};

const performBlobUpload = async ({ url, fields, blob, onUploadProgress }) => {
  const formData = new FormData();

  for (const field in fields) {
    formData.append(field, fields[field]);
  }

  formData.append('file', blob);

  const reqOptions = {};
  if (onUploadProgress) reqOptions.onUploadProgress = onUploadProgress;

  await axios.post(url, formData, reqOptions);
};

export default useDirectUpload;
